import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuthStore } from "@/features/auth/store/auth-store";
import * as opsApi from "@/features/ops/api";
import type { JobRun, JobSpec } from "@/features/ops/types";

function statusVariant(status?: string): "default" | "secondary" | "destructive" {
  if (status === "SUCCESS") return "default";
  if (status === "FAILED") return "destructive";
  return "secondary";
}

export default function JobsPage() {
  const [jobs, setJobs] = useState<JobSpec[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState<string | null>(null);
  const [history, setHistory] = useState<{ name: string; runs: JobRun[] } | null>(null);
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser ?? false);

  const load = async () => {
    setLoading(true);
    try {
      setJobs(await opsApi.listJobs());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const run = async (name: string) => {
    setRunning(name);
    try {
      await opsApi.runJob(name);
      toast.success(`Enqueued ${name}`);
      // Give the worker a moment, then refresh last-run.
      setTimeout(() => void load(), 800);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not enqueue job");
    } finally {
      setRunning(null);
    }
  };

  const openHistory = async (name: string) => {
    try {
      const res = await opsApi.getJobRuns(name);
      setHistory({ name, runs: res.runs });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load history");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">
        Scheduled background jobs (via Celery beat). Superusers can trigger a run on demand.
      </p>
      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Job</TableHead>
              <TableHead>Cadence</TableHead>
              <TableHead>Last run</TableHead>
              <TableHead>Result</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground py-8 text-center">
                  Loading…
                </TableCell>
              </TableRow>
            ) : (
              jobs.map((job) => (
                <TableRow key={job.name}>
                  <TableCell>
                    <div className="font-medium">{job.label}</div>
                    <div className="text-muted-foreground text-xs">{job.description}</div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{job.cadence}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {job.lastRun ? new Date(job.lastRun.startedAt).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell>
                    {job.lastRun ? (
                      <Badge variant={statusVariant(job.lastRun.status)}>
                        {job.lastRun.status}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">never run</span>
                    )}
                  </TableCell>
                  <TableCell className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => openHistory(job.name)}>
                      History
                    </Button>
                    {isSuperuser && job.manual && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={running === job.name}
                        onClick={() => run(job.name)}
                      >
                        {running === job.name ? "Running…" : "Run now"}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Run history — {history?.name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Records</TableHead>
                  <TableHead>Summary</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history?.runs.length ? (
                  history.runs.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap">
                        {new Date(r.startedAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(r.status)}>{r.status}</Badge>
                      </TableCell>
                      <TableCell>{r.recordsProcessed}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {r.error || r.summary}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-muted-foreground py-6 text-center">
                      No runs yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
